/**
 * Pending Review rule filters: "Unmapped type" (unmapped=true) and
 * "No woocommerce tag" (sell_in_app=false). Both also drop field-change-only
 * rows server-side. An absent key means the filter is off.
 */
import React from 'react';
import { Chip, Stack, alpha } from '@mui/material';
import { useAdminTheme } from '../../admin-theme';
import { stagingOrg } from './stagingOrg';

export interface PendingReviewFilterValue {
  unmapped?: true;
  sellInApp?: false;
}

/** Query params for GET vaperite/staging/items. */
export function pendingReviewFilterParams(value: PendingReviewFilterValue): Record<string, boolean> {
  const params: Record<string, boolean> = {};
  if (value.unmapped) params.unmapped = true;
  if (value.sellInApp === false) params.sell_in_app = false;
  return params;
}

interface Props {
  value: PendingReviewFilterValue;
  onChange: (next: PendingReviewFilterValue) => void;
}

const PendingReviewFilters: React.FC<Props> = ({ value, onChange }) => {
  const { colors } = useAdminTheme();

  const toggles: Array<{ label: string; active: boolean; next: () => PendingReviewFilterValue }> = [
    {
      label: 'Unmapped type',
      active: value.unmapped === true,
      next: () => {
        const { unmapped, ...rest } = value;
        return unmapped ? rest : { ...rest, unmapped: true };
      },
    },
    {
      // Deeliver's Sell in app is the hide-in-app Vend tag; Vaperite's is their woocommerce tag.
      label: stagingOrg() === 'deeliver' ? 'Hidden (hide-in-app)' : 'No woocommerce tag',
      active: value.sellInApp === false,
      next: () => {
        const { sellInApp, ...rest } = value;
        return sellInApp === false ? rest : { ...rest, sellInApp: false };
      },
    },
  ];

  return (
    <Stack direction="row" spacing={1}>
      {toggles.map((t) => (
        <Chip
          key={t.label}
          label={t.label}
          size="small"
          clickable
          aria-pressed={t.active}
          onClick={() => onChange(t.next())}
          sx={{
            height: 28,
            fontSize: '0.72rem',
            fontWeight: 600,
            bgcolor: t.active ? alpha(colors.warning, 0.15) : 'transparent',
            color: t.active ? colors.warning : colors.textSecondary,
            border: `1px solid ${t.active ? alpha(colors.warning, 0.5) : colors.border}`,
          }}
        />
      ))}
    </Stack>
  );
};

export default PendingReviewFilters;
